import { c as _c } from "react/compiler-runtime";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useAppParams } from "@router/router-helper";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector, loggedInUserSelector } from "@store/user/user-selector";
import { getBreakpointName } from "@utils/ui-utils";
import { useLocation } from "react-router-dom";

/** Params for tracking an event */

/** All the ids available in the app */
const allEntityIds = ["companyId", "projectId", "memberId", "groupId"];

/** Default properties for the event */

/** Track events with additional default properties that might be changed over the course */
export function useTrackEvent() {
  const $ = _c(11);
  const {
    pathname
  } = useLocation();
  const params = useAppParams();
  const currentUser = useAppSelector(currentUserSelector);
  const selectedProject = useAppSelector(selectedProjectSelector);
  const loggedInUser = useAppSelector(loggedInUserSelector);
  let t0;
  if ($[0] !== currentUser?.role || $[1] !== loggedInUser?.id || $[2] !== params || $[3] !== pathname || $[4] !== selectedProject?.role) {
    const getProps = function getProps(props) {
      const defaultProps = {
        windowWidthBreakpoint: getBreakpointName(window.innerWidth),
        workspaceRole: currentUser?.role?.toString(),
        projectRole: selectedProject?.role?.toString(),
        path: createPathForTracking(pathname, params),
        userId: loggedInUser?.id
      };
      return {
        ...defaultProps,
        ...props
      };
    };
    t0 = function trackEvent(t1) {
      const {
        name,
        props: props_0
      } = t1;
      Analytics.track(name, getProps(props_0));
    };
    $[0] = currentUser?.role;
    $[1] = loggedInUser?.id;
    $[2] = params;
    $[3] = pathname;
    $[4] = selectedProject?.role;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const trackEvent = t0;
  let t1;
  if ($[6] !== trackEvent) {
    t1 = async function trackAsyncEvent(t2) {
      const {
        name: name_0,
        props: props_1
      } = t2;
      trackEvent({
        name: name_0,
        props: props_1
      });
      return Promise.resolve();
    };
    $[6] = trackEvent;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const trackAsyncEvent = t1;
  let t2;
  if ($[8] !== trackAsyncEvent || $[9] !== trackEvent) {
    t2 = {
      trackEvent,
      trackAsyncEvent
    };
    $[8] = trackAsyncEvent;
    $[9] = trackEvent;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

/**
 * Creates the path parameter for tracking by replacing the actual entityIds with its name
 * For example the ID of company changes to "companyId" in the pathname
 * encodeURIComponent is used to replaces the encoded dynamicId with staticId in the pathname.
 * If memberId is foo@bar in the pathname it becomes foo%40bar and need to be encoded to be replaced with memberId
 */
export function createPathForTracking(originalPathname, params) {
  try {
    let replacedPath = originalPathname;
    for (const [paramName, paramValue] of Object.entries(params)) {
      if (allEntityIds.includes(paramName)) {
        // Replaces the encoded dynamicId with staticId in the pathname.
        // This is used as some characters are encoded in the pathname, e.g. foo@bar -> foo%40bar
        replacedPath = replacedPath.replace(encodeURIComponent(paramValue), paramName);
      }
    }
    return replacedPath;
  } catch (error) {
    // If there is an error in the function, log the error and return an empty string
    // Therefore the app doesn't crash and no sensitive data like project id is sent to the analytic service
    // eslint-disable-next-line no-console
    console.error("Error in createPathForTracking", error);
    return "";
  }
}